// eslint-disable-next-line @nx/enforce-module-boundaries
import { getStyleClassPerTheme, ImageAspectRatio } from '@marriott/mi-ui-library';
import {
  DcaMetadata,
  LayeredFeatureAdapterCtx,
  LayeredFeatureCfContent,
  LayeredFeatureCfPayload,
  LayeredFeatureContentPayload,
  LayeredFeatureDcaProps,
  LayeredFeatureDCAPayload,
  LayeredFeatureTrackingProperties,
  isCoBrandDca,
  isDcaContent,
  RenditionList,
} from './adapter.types';
import { getRecentlyViewedImageFallbackForPayload } from '../dynamicContentUtils';
import {
  parseDcaImpressionValue,
  resolveDcaMboxTrackingPosition,
  resolveImageUrl,
  type SlotTrackingContext,
  isExternalLink,
} from '../slotPropNormalizerUtils';
import { buildCardTrackingDescription, resolveCardCategory, resolveCardDescription } from '../polarisTrackingUtils';
import { buildLayeredFeatureDcaRenditions, buildLayeredFeatureDynamicMediaNode } from '../dynamicMediaUtils';

const MATH_FIELDS = ['mathLabel1', 'mathLabel2', 'mathLabel3', 'mathValue', 'mathType', 'mathTitle'] as const;

function resolveOnlyTruthyImageUrls(imageUrls: Record<string, string | null>): Record<string, string> {
  return Object.fromEntries(Object.entries(imageUrls).map(([key, value]) => [key, resolveImageUrl(value ?? '')]));
}

function buildLayeredFeatureTracking(
  trackingCtx: SlotTrackingContext | undefined,
  slotType: 'cf' | 'dca' | string | undefined,
  contentObj: LayeredFeatureContentPayload,
  slotMboxParameter?: string
): { trackingProperties?: LayeredFeatureTrackingProperties; custom_click_track_value_primary?: string } {
  if (!trackingCtx) return {};
  const { tabIndex = 1, tabName = '', index: cardNumber = 0, slotSize = 'banner' } = trackingCtx;
  const tabTrackingContentPosition = trackingCtx.trackingProperties?.['tabTrackingContentPosition'] || '';
  const trackingContent = contentObj as Record<string, unknown>;
  const dcaImpressionTracking = parseDcaImpressionValue(trackingContent['impressionValue']);
  const dcaMboxPosition =
    resolveDcaMboxTrackingPosition({ mboxParameter: slotMboxParameter }) ||
    resolveDcaMboxTrackingPosition(trackingContent);
  const position =
    slotType === 'dca'
      ? dcaMboxPosition ||
        dcaImpressionTracking.trackingContentPosition ||
        (trackingContent['trackingContentPosition'] as string) ||
        tabTrackingContentPosition ||
        trackingCtx.trackingProperties?.trackingContentPosition ||
        ''
      : tabTrackingContentPosition || trackingCtx.trackingProperties?.trackingContentPosition || '';
  const category = resolveCardCategory(trackingContent, slotType);
  const description = resolveCardDescription(trackingContent, slotType);

  const cardTrackingDesc = buildCardTrackingDescription({
    tabIndex,
    tabName,
    cardNumber,
    shape: slotSize,
    category,
    description,
  });

  const metadata =
    slotType === 'dca'
      ? ((trackingContent['metaData'] ?? trackingContent['metadata']) as Record<string, unknown> | undefined)
      : undefined;
  const isCoBrand = metadata?.['type']?.toString().toLowerCase() === 'cobrand';

  // WEB-191033: CF (AEM-authored) layered-feature banners. The previous implementation
  // pre-populated `custom_click_track_value_primary` with `${pos}|${desc}|internal`, which
  // short-circuited `useTracking` -> `getClickTrackValue` in MButton (see useTracking.ts:93
  // `if (!custom_click_track_value)`) and suppressed every merch event token. Launch then
  // only saw the base `event2` on click. We now leave the CTV undefined so
  // `getClickTrackValue` runs at runtime and stamps the correct tail — `event167,list3=...`
  // for non-cobrand, or `event167,event271,evar48=<CCOFFER>` for CF cards whose authored
  // destination URL is a cobrand landing page. Cobrand detection is done from the CTA href
  // because CF cards have no Target mbox metadata to read.
  //
  // Scope: primary CTA only. The secondary CTA in MerchandisingLayeredFeature.tsx:313-315
  // still hardcodes its own tailless CTV and is NOT fixed by this change.
  if (slotType !== 'dca') {
    const cfContent = trackingContent;
    const ctaHref =
      (typeof cfContent['primaryCtaLink'] === 'string' && (cfContent['primaryCtaLink'] as string)) ||
      (typeof cfContent['primaryCTALink'] === 'string' && (cfContent['primaryCTALink'] as string)) ||
      (typeof cfContent['secondaryCtaLink'] === 'string' && (cfContent['secondaryCtaLink'] as string)) ||
      '';
    const isCobrandHref = /\/mi\/cobrand\//i.test(ctaHref) || /[?&]mcd=CCOFFER-/i.test(ctaHref);
    const ccofferFromHref = ctaHref.match(/[?&]mcd=(CCOFFER-[^&#]+)/i)?.[1] ?? '';

    const trackingPropsCf: LayeredFeatureTrackingProperties = {
      trackingContentPosition: position,
      trackingDescription: cardTrackingDesc,
      clickTrack: 'true',
      impressionTrack: 'true',
      impressionCount: 'true',
    };

    if (isCobrandHref) {
      trackingPropsCf['isCoBrand'] = 'true';
      trackingPropsCf['type'] = 'CoBrand';
      if (ccofferFromHref) {
        trackingPropsCf['atCCeVar48'] = ccofferFromHref;
      }
    } else {
      trackingPropsCf['trackingOfferType'] = 'merch';
    }

    return {
      trackingProperties: trackingPropsCf,
      custom_click_track_value_primary: undefined,
    };
  }

  // WEB-191033: DCA banners. Cobrand must keep the runtime path because that is
  // the only path that fires `event168|event270|evar48=<id>`. Non-cobrand LFC
  // needs a split contract: impression tracking uses Target's DCA fields
  // (`msg=dca`, `tag=...`), while click tracking uses the Polaris card
  // description for eVar22 and keeps the DCA tag token for downstream Launch rules.
  const trackingProps: LayeredFeatureTrackingProperties = {
    trackingContentPosition: position,
    trackingDescription: metadata ? dcaImpressionTracking.trackingDescription || 'dca' : cardTrackingDesc,
    clickTrack: 'true',
    impressionTrack: 'true',
    impressionCount: 'true',
    isDCAEnabled: 'true',
  };

  if (isCoBrand) {
    const evar48 = (
      (trackingContent['atCCeVar48'] || trackingContent['eVar48'] || metadata?.['evar48'] || '') as string
    ).trim();
    if (evar48) {
      trackingProps['atCCeVar48'] = evar48;
    }
    trackingProps['isCoBrand'] = 'true';
    trackingProps['type'] = 'CoBrand';
  } else {
    const offerType =
      (trackingContent['trackingOfferType'] as string) ||
      dcaImpressionTracking.trackingOfferType ||
      (metadata?.['type'] as string) ||
      'merch';
    const offerCodeTag = (metadata?.['offerCode'] as string) || '';
    const trackingTag =
      (trackingContent['trackingTag'] as string) ||
      (metadata?.['merTag'] as string) ||
      offerCodeTag ||
      dcaImpressionTracking.trackingTag;
    // Fallback: Target sends the authoritative tag inside `impressionValue` when
    // `metadata.offerCode` is absent (e.g. promo-tag content).
    trackingProps['trackingOfferType'] = offerType;
    (trackingProps as Record<string, unknown>)['impressionTrack'] = false;
    (trackingProps as Record<string, unknown>)['impressionCount'] = false;
    if (trackingTag) trackingProps['trackingTag'] = trackingTag;
  }

  const destinationUrl =
    typeof trackingContent['destinationUrl1'] === 'string' ? trackingContent['destinationUrl1'] : '';
  const openInNewTab = trackingContent['openInNewTab1'] === true;
  const linkType = isExternalLink(destinationUrl, openInNewTab) ? 'external' : 'internal';
  const clickTrackingTag = trackingProps['trackingTag'] ? `;tag=${trackingProps['trackingTag']}` : '';
  const nonCobrandClickValue = !isCoBrand
    ? `${position}|${cardTrackingDesc}|${linkType}|event167,list3=${position};type=${
        trackingProps['trackingOfferType'] || ''
      };msg=${trackingProps['trackingDescription'] || ''}${clickTrackingTag}`
    : undefined;

  return {
    trackingProperties: trackingProps,
    custom_click_track_value_primary: nonCobrandClickValue,
  };
}

function getCfDescription(cf: LayeredFeatureCfContent): string {
  const { description } = cf;
  if (typeof description === 'string') return description;
  if (description?.plaintext) return description.plaintext;
  return '';
}

function adaptContentFragmentToDcaProps(ctx: LayeredFeatureCfPayload): LayeredFeatureDcaProps {
  const cf = ctx.contentObj;
  const header = cf.header ?? cf.title ?? '';
  const description = getCfDescription(cf);
  const className = cf.styleclass ?? cf.styleClass ?? '';
  const cfAssetPath = cf.image?.assetPath ?? cf.assetPath;
  const cfAltText = cf.image?.altText ?? cf.altText ?? ctx.alt;
  const dynamicMedia = buildLayeredFeatureDynamicMediaNode(cfAssetPath, cfAltText);
  const backgroundImage = {
    imageSrc: resolveImageUrl(dynamicMedia?.assetPath ?? ''),
    alternateDescription: dynamicMedia?.altText ?? '',
    title: cf.title ?? '',
    defaultImage: false,
    imageUrls: {},
    renditions: (dynamicMedia?.renditions ?? []) as RenditionList,
  };
  const mcomRegex = /^mcom-[^:]+:(?:brands?|general)\//;
  const isLogoBrandToken = !!cf.icon?.match(mcomRegex);
  const theme = getStyleClassPerTheme(cf?.theme) || undefined;
  const showEyebrow = !cf.icon;

  return {
    eyebrowText: showEyebrow ? cf.eyebrow ?? '' : '',
    headerLong: header,
    headerMedium: header,
    headerShort: header,
    descriptionLong: description,
    descriptionMedium: description,
    descriptionShort: description,
    callToActionText1: cf.primaryCTALinkText || cf.primaryCtaLinkText,
    destinationUrl1: cf.primaryCTALink || cf.primaryCtaLink,
    callToActionSize1: cf.primaryCtaSize,
    callToActionSize2: cf.secondaryCtaSize,
    callToActionText2: cf.secondaryCtaLinkText,
    destinationUrl2: cf.secondaryCtaLink,
    openInNewTab1: cf.primaryCtaOpenInNewTab,
    openInNewTab2: cf.secondaryCtaOpenInNewTab,
    primaryCtaAriaLabel: cf.primaryCtaAriaLabel,
    secondaryCtaAriaLabel: cf.secondaryCtaAriaLabel,
    tertiaryLinkText: cf.tertiaryCtaLinkText ?? '',
    tertiaryLinkUrl: cf.tertiaryCtaLink ?? '',
    tertiaryOpenInANewTab: cf.tertiaryOpenInANewTab ?? false,
    tertiaryLinkAriaLabel: cf.tertiaryLinkAriaLabel ?? '',
    backgroundImage,
    desktopVariation: cf.desktopVariation ?? ImageAspectRatio.SIZE_3x1,
    tabletVariation: cf.tabletVariation ?? ImageAspectRatio.SIZE_2X1,
    mobileVariation: cf.mobileVariation ?? ImageAspectRatio.SIZE_1x1,
    className,
    logo: {
      imageSrc: isLogoBrandToken ? cf.icon || '' : resolveImageUrl(cf.icon ?? ''),
      title: '',
      alternateDescription: cf.logoImageAltText ?? '',
      defaultImage: false,
      imageUrls: {},
      renditions: [],
    },
    componentId: cf.componentId,
    trackingProperties: cf.trackingProperties,
    custom_click_track_value_primary: cf.custom_click_track_value_primary,
    caption: cf.photoCaption ?? '',
    theme,
    type: 'cf' as const,
  };
}

function adaptDcaBaseToDcaProps(ctx: LayeredFeatureDCAPayload): LayeredFeatureDcaProps {
  const dca = ctx.contentObj;
  const recentlyViewedRectangleFallback = getRecentlyViewedImageFallbackForPayload(
    dca as Record<string, unknown>,
    'rectangle'
  );
  const recentlyViewedStandardFallback = getRecentlyViewedImageFallbackForPayload(
    dca as Record<string, unknown>,
    'standardHorizontal'
  );
  const recentlyViewedPanoramicFallback = getRecentlyViewedImageFallbackForPayload(
    dca as Record<string, unknown>,
    'panoramicHorizontal'
  );
  const recentlyViewedSquareFallback = getRecentlyViewedImageFallbackForPayload(
    dca as Record<string, unknown>,
    'square'
  );
  const backgroundImageUrls = resolveOnlyTruthyImageUrls({
    ...(dca.image?.imageUrls ?? {}),
    ...(recentlyViewedPanoramicFallback ? { panoramicHorizontal: recentlyViewedPanoramicFallback } : {}),
    ...(recentlyViewedStandardFallback ? { standardHorizontal: recentlyViewedStandardFallback } : {}),
    ...(recentlyViewedSquareFallback ? { square: recentlyViewedSquareFallback } : {}),
  });
  const backgroundImageSrc = resolveImageUrl(dca.image?.imageSrc ?? recentlyViewedRectangleFallback ?? '');
  const backgroundImageRenditions = buildLayeredFeatureDcaRenditions(
    backgroundImageUrls,
    backgroundImageSrc
  ) as RenditionList;
  const metadata = dca.metadata as DcaMetadata | undefined;
  const iconRegex = /^(?:mcom-[^:]+:(?:brands)\/|marriott-icons:.+)/i;
  const isLogoBrandToken = !!dca.logo?.imageSrc?.match(iconRegex);
  const showEyebrow = !dca.logo?.imageSrc;

  return {
    eyebrowText: showEyebrow ? dca.eyebrowText ?? '' : '',
    headerMedium: dca.headerMedium,
    headerShort: dca.headerShort,
    descriptionLong: dca.descriptionLong,
    descriptionMedium: dca.descriptionMedium,
    descriptionShort: dca.descriptionShort,
    callToActionText1: dca.callToActionText1,
    destinationUrl1: dca.destinationUrl1,
    openInNewTab1: isExternalLink(dca.destinationUrl1 ?? '', false),
    callToActionText2: dca.callToActionText2,
    destinationUrl2: dca.destinationUrl2,
    openInNewTab2: isExternalLink(dca.destinationUrl2 ?? '', false),
    primaryCtaAriaLabel: dca.primaryCtaAriaLabel,
    secondaryCtaAriaLabel: dca.secondaryCtaAriaLabel,
    backgroundImage: {
      imageUrls: backgroundImageUrls,
      imageSrc: backgroundImageSrc,
      title: dca.image?.title ?? '',
      alternateDescription: dca.image?.alternateDescription ?? '',
      defaultImage: dca.image?.defaultImage ?? false,
      renditions: backgroundImageRenditions,
    },
    desktopVariation: ImageAspectRatio.SIZE_3x1,
    tabletVariation: ImageAspectRatio.SIZE_2X1,
    mobileVariation: ImageAspectRatio.SIZE_1x1,
    logo: {
      imageSrc: isLogoBrandToken ? dca.logo?.imageSrc || '' : resolveImageUrl(dca.logo?.imageSrc ?? ''),
      title: dca.logo?.title ?? '',
      alternateDescription: dca.logo?.alternateDescription ?? '',
      defaultImage: dca.logo?.defaultImage ?? false,
      imageUrls: resolveOnlyTruthyImageUrls(dca.logo?.imageUrls ?? {}),
      renditions: [],
    },
    caption: dca.caption ?? dca.photoCaption ?? '',
    theme: getStyleClassPerTheme(metadata?.theme) || undefined,
    type: 'dca' as const,
  };
}

function hasNonEmptyMathValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(entry => hasNonEmptyMathValue(entry));
  return true;
}

export function hasMathValues(contentObj: Record<string, unknown>): boolean {
  return MATH_FIELDS.some(field => hasNonEmptyMathValue(contentObj[field]));
}

export function pickLayeredFeaturePropsConfiguration(
  ctx: LayeredFeatureAdapterCtx
): (ctx: LayeredFeatureAdapterCtx) => LayeredFeatureDcaProps {
  if (isCoBrandDca(ctx)) {
    return adaptDcaCoBrandToLayeredFeature;
  } else if (isDcaContent(ctx)) {
    return adaptDcaBaseToLayeredFeature;
  }
  return adaptContentFragmentToLayeredFeature;
}

export function adaptContentFragmentToLayeredFeature(ctx: LayeredFeatureCfPayload): LayeredFeatureDcaProps {
  const baseProps = adaptContentFragmentToDcaProps(ctx);
  const tracking = buildLayeredFeatureTracking(
    ctx.trackingCtx,
    ctx.slot?.type,
    ctx.contentObj,
    ctx.slot?.dcaTarget?.mboxParameter
  );
  return {
    ...baseProps,
    trackingProperties: tracking.trackingProperties ?? baseProps.trackingProperties,
    custom_click_track_value_primary:
      tracking.custom_click_track_value_primary ?? baseProps.custom_click_track_value_primary,
  };
}

export function adaptDcaBaseToLayeredFeature(ctx: LayeredFeatureDCAPayload): LayeredFeatureDcaProps {
  const baseProps = adaptDcaBaseToDcaProps(ctx);
  const tracking = buildLayeredFeatureTracking(
    ctx.trackingCtx,
    ctx.slot?.type,
    ctx.contentObj,
    ctx.slot?.dcaTarget?.mboxParameter
  );
  return {
    ...baseProps,
    trackingProperties: tracking.trackingProperties,
    custom_click_track_value_primary: tracking.custom_click_track_value_primary,
  };
}

export function adaptDcaCoBrandToLayeredFeature(ctx: LayeredFeatureDCAPayload): LayeredFeatureDcaProps {
  const dca = ctx.contentObj;
  const metadata = dca.metadata as DcaMetadata | undefined;
  const baseProps = adaptDcaBaseToDcaProps(ctx);
  const tracking = buildLayeredFeatureTracking(
    ctx.trackingCtx,
    ctx.slot?.type,
    ctx.contentObj,
    ctx.slot?.dcaTarget?.mboxParameter
  );

  return {
    ...baseProps,
    mathType: dca.mathType ?? undefined,
    mathLabel1: dca.mathLabel1 ?? undefined,
    mathLabel2: dca.mathLabel2 ?? undefined,
    mathLabel3: dca.mathLabel3 ?? undefined,
    mathValue: dca.mathValue ?? undefined,
    mathTitle: dca.mathTitle ?? undefined,
    legalLong: dca.legalLong ?? undefined,
    metadata: metadata ?? undefined,
    mboxParameter: dca.mboxName ?? undefined,
    isTargetContentLoaded: dca.isTargetContentLoaded ?? true,
    trackingProperties: tracking.trackingProperties,
    custom_click_track_value_primary: tracking.custom_click_track_value_primary,
  };
}
